import { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import useApiHealth from "../hooks/useApiHealth";
import useAuthenticatedWebSocket from "../hooks/useAuthenticatedWebSocket";
import { useAuthenticatedApi } from "../hooks/useAuthenticatedApi";
import useWebSocketThreadUrl from "../hooks/useWebSocketThreadUrl";
import { useThreadActions, useThreadsStore } from "../store/threadsStore";
import useMessages from "../hooks/useMessages";
import useTerminalState from "../hooks/useTerminalState";
import useWorkspace from "../hooks/useWorkspace";
import { useSandbox } from "../hooks/useSandbox";
import { useSandboxStore } from "../store/sandboxStore";
import { useNavigate } from "react-router-dom";
import { useSettingsModal } from "../contexts/SettingsModalContext";
import { contentToString } from "./chat/Renderers";
import {
  getAgentTypeLabel,
  getAgentTypeIcon,
  getAgentTypeColor,
  convertMessagesToFrontend,
} from "../utils/agentUtils";
import { applyAgentEventToMessages } from "../utils/agentEventProjection";

import ThreadsPanel from "./ThreadsPanel.jsx";
import ChatArea from "./chat/chat-area";
import ToolsPanel from "./chat/ToolsPanel";
import { MobileToolsSubheaderButtons } from "./MobileToolsSubheader";
import MobileToolsSubheader from "./MobileToolsSubheader";
import { styling } from "./chat/chatStyles.js";
import { HelpSystem, useHelp } from "./tour";
import { useReadOnlyMode } from "../hooks/useReadOnlyMode";
import MemorySuggestionPopup from "./chat/MemorySuggestionPopup";

import { MAX_FILE_SIZE, MAX_FILE_SIZE_IN_MB } from "./chat/chat-area/input-area/UploadedFiles";

const ThreadInterface = ({ workspaceId: workspaceIdProp, threadId }) => {
  const [agentType, setAgentType] = useState("coding_agent");
  const [projectPath, setProjectPath] = useState("Loading...");
  const [selectedToolTab, setSelectedToolTab] = useState("preview");
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Default to collapsed on mobile (< 768px)
    return window.innerWidth >= 768;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [latestSystemMessage, setLatestSystemMessage] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  
  // Memory suggestions state
  const [memorySuggestions, setMemorySuggestions] = useState([]);
  const [dismissedSuggestionIndices, setDismissedSuggestionIndices] = useState(new Set());
  
  // Track if screen is mobile sized
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Mobile tool overlay state
  const [mobileToolOverlay, setMobileToolOverlay] = useState(null);
  // Settings modal hook
  const { openProjectSettings } = useSettingsModal();

  const [splitWidth, setSplitWidth] = useState(36);
  const [isDragging, setIsDragging] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(null);
  const mainContainerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const resizerRef = useRef(null);

  const [logs, setLogs] = useState([
    {
      level: "info",
      content: "Kolega Chat initialized",
      timestamp: new Date(),
    },
    {
      level: "info",
      content: "Ready to assist with software development tasks",
      timestamp: new Date(),
    },
  ]);
  const [terminalTabs, setTerminalTabs] = useState([]); // [{ id: terminal_id, history: [] }]
  const [activeTerminalTab, setActiveTerminalTab] = useState(null);
  const [expandedTools, setExpandedTools] = useState({});
  
  // Terminal optimization states
  const terminalBufferRef = useRef([]);
  const terminalBatchTimeoutRef = useRef(null);
  const MAX_TERMINAL_LINES = 1000; // Configurable limit

  const processedThreadsRef = useRef(new Set()); // Synchronous tracking

  // Reference to hold queued initial message (if any)
  const queuedInitialMessageRef = useRef(null);

  const [showLLMErrorIndicator, setShowLLMErrorIndicator] = useState(false);
  const [LLMErrorIndicatorMessage, setLLMErrorIndicatorMessage] = useState('');
  const { isReadOnly } = useReadOnlyMode();
  
  // Context usage tracking state
  const [contextUsage, setContextUsage] = useState(null);

  const navigate = useNavigate();
  const { isHelpModeActive } = useHelp();
  const api = useAuthenticatedApi();

  // Handle mobile view detection and resize
  useEffect(() => {
    const handleResize = () => {
      // Check if mobile view (less than 768px - md breakpoint)
      const mobileViewDetected = window.innerWidth < 768;
      setIsMobileView(mobileViewDetected);
    };

    // Check on initial load
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { getThread, updateThread, createThread } = useThreadActions();

  // Use the messages hook instead of local state
  const {
    messages,
    setMessages,
    loading: messagesLoading,
    error: messagesError,
    getMessages,
  } = useMessages(workspaceIdProp, threadId);

  // Use terminal state hook to restore saved terminal state
  const { terminalState, loading: terminalStateLoading, error: terminalStateError } = useTerminalState(workspaceIdProp);

  // Use workspace hook to fetch workspace data
  const { getWorkspaceById } = useWorkspace();

  // Use sandbox hook to monitor sandbox status changes
  const { sandboxStatus } = useSandbox(workspaceIdProp);
  const isSandboxReady =
    sandboxStatus.status === 'ready' || sandboxStatus.status === 'not_applicable';
  const effectiveSandboxHost = sandboxStatus.status === 'ready' && workspace?.sandbox_host
    ? workspace.sandbox_host
    : '';
  const setWorkspaceAgentActivity = useSandboxStore(state => state.setWorkspaceAgentActivity);

  // Initialize threads store polling
  useEffect(() => {
    if (workspaceIdProp) {
      // Start global threads polling (this will fetch threads automatically)
      const threadsStore = useThreadsStore.getState();
      threadsStore.startPolling(workspaceIdProp, api);
      
      return () => {
        threadsStore.stopPolling();
      };
    }
  }, [workspaceIdProp, api]);

  // Restore terminal state when it's loaded
  useEffect(() => {
    if (terminalState && terminalState.terminals && terminalState.terminals.length > 0) {
      setTerminalTabs(terminalState.terminals);
      // Set the first terminal as active if none is set
      if (!activeTerminalTab && terminalState.terminals.length > 0) {
        setActiveTerminalTab(terminalState.terminals[0].id);
      }
      
      setLogs((prev) => [
        ...prev,
        {
          level: "info",
          content: `Restored ${terminalState.terminals.length} terminal session(s)`,
          timestamp: new Date(),
        },
      ]);
    }
  }, [terminalState]);

  const [theThread, setTheThread] = useState(null);

  const [input, setInput] = useState("");
  const [agentStatus, setAgentStatus] = useState("stopped");
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    if (!workspaceIdProp) {
      return;
    }
    setWorkspaceAgentActivity(workspaceIdProp, agentStatus === 'generating');
    return () => {
      setWorkspaceAgentActivity(workspaceIdProp, false);
    };
  }, [workspaceIdProp, agentStatus, setWorkspaceAgentActivity]);

  const fileInputRef = useRef(null);
  const chatRef = useRef(null);
  const logRef = useRef(null);
  const terminalRef = useRef(null);
  const toolsPanelRef = useRef(null);

  const reconnectAttemptRef = useRef(0);
  const workspaceFetchedRef = useRef(false);

  const { isHealthy } = useApiHealth(5000);
  const prevHealthyRef = useRef(null);
  // Track the last sandbox ID we refreshed for to prevent duplicate refreshes
  const lastRefreshedSandboxIdRef = useRef(null);

  //   const eventUrl = useWebSocketUrl(workspaceIdProp, agentType);
  const eventUrl = useWebSocketThreadUrl(workspaceIdProp, threadId, agentType);

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const getTextContent = (content) => {
    if (!content) return "";
    if (typeof content === "string") return content;
    if (typeof content === "object" && content.text) return content.text;
    return contentToString(content);
  };

  const handleTranscriptEvent = (message, currentAgentType) => {
    setMessages((previousMessages) =>
      applyAgentEventToMessages(previousMessages, message, currentAgentType)
    );
  };

  const handleLogMessage = (event) => {
    setLogs((prev) => [
      ...prev,
      {
        level:
          event.content && event.content.level ? event.content.level : "info",
        content:
          event.content && event.content.text
            ? event.content.text
            : getTextContent(event.content),
        timestamp: new Date(event.timestamp) || new Date(),
      },
    ]);
  };

  const handleTerminalMessage = (event) => {
    const terminalId = event.content.terminal_id || "default";
    
    // Add to buffer instead of updating immediately
    terminalBufferRef.current.push({ terminalId, event });
    
    // Clear existing timeout
    if (terminalBatchTimeoutRef.current) {
      clearTimeout(terminalBatchTimeoutRef.current);
    }
    
    // Set new timeout to batch updates (50ms batching window)
    terminalBatchTimeoutRef.current = setTimeout(() => {
      flushTerminalBuffer();
    }, 50);
  };

  const flushTerminalBuffer = useCallback(() => {
    // Get current buffer and clear it
    const currentBuffer = [...terminalBufferRef.current];
    terminalBufferRef.current = [];
    
    if (currentBuffer.length === 0) return;
    
    setTerminalTabs((prevTabs) => {
      const updates = new Map();
      
      // Process all buffered messages
      currentBuffer.forEach(({ terminalId, event }) => {
        if (!updates.has(terminalId)) {
          const existingTab = prevTabs.find(tab => tab.id === terminalId);
          // Initialize with existing content as a single string, or empty string
          const existingContent = existingTab?.content || '';
          updates.set(terminalId, existingContent);
        }
        
        let content = updates.get(terminalId);
        if (event.event_type === "terminal_command") {
          // Add command on a new line (unless it's the very first content)
          content += (content ? '\n' : '') + `$ ${event.content.command}`;
        } else if (event.event_type === "terminal_output") {
          // Append output directly without adding extra newlines
          content += event.content.output;
        }
        updates.set(terminalId, content);
      });
      
      // Apply all updates at once
      let newTabs = prevTabs.map(tab => {
        if (updates.has(tab.id)) {
          let updatedContent = updates.get(tab.id);
          
          // Apply character limit to prevent unbounded growth
          const MAX_TERMINAL_CHARS = 100000; // 100k characters
          if (updatedContent.length > MAX_TERMINAL_CHARS) {
            // Keep only the most recent content
            const truncateAt = updatedContent.length - MAX_TERMINAL_CHARS + 100; // Leave some buffer
            updatedContent = '... (older output truncated) ...\n' + updatedContent.substring(truncateAt);
          }
          
          return { ...tab, content: updatedContent };
        }
        return tab;
      });
      
      // Add new tabs if needed
      updates.forEach((content, terminalId) => {
        if (!prevTabs.some(tab => tab.id === terminalId)) {
          newTabs.push({ id: terminalId, content });
        }
      });
      
      return newTabs;
    });
    
    // Set active terminal tab to the most recently updated one
    if (currentBuffer.length > 0) {
      setActiveTerminalTab(currentBuffer[currentBuffer.length - 1].terminalId);
    }
  }, [MAX_TERMINAL_LINES]);

  // Cleanup batch timeout on unmount
  useEffect(() => {
    return () => {
      if (terminalBatchTimeoutRef.current) {
        clearTimeout(terminalBatchTimeoutRef.current);
        flushTerminalBuffer();
      }
    };
  }, []);

  const handleToolStreamingUpdate = useCallback((event) => {
    const { tool_call_id, text, tool_name, is_complete } = event.content;
    
    console.log('handleToolStreamingUpdate', {tool_call_id, tool_name, is_complete, textLength: text?.length});
    
    // Update the message with streaming content
    setMessages(prevMessages => {
      const updatedMessages = prevMessages.map((msg) => {
        if ((msg.tool_call_id === tool_call_id || msg.toolCallId === tool_call_id) && 
            (msg.message_type === 'tool_call' || msg.isToolCall)) {
          
          // Accumulate streaming content
          const previousContent = msg.toolStatus?.streamingContent || '';
          const newContent = text || '';
          
          // If this is the final complete update, use the text directly (it contains everything)
          // Otherwise accumulate the chunks
          const accumulatedContent = is_complete ? newContent : previousContent + newContent;
          
          console.log(`Updating tool call message with streaming content (previous: ${previousContent.length}, new chunk: ${newContent.length}, total: ${accumulatedContent.length} chars)`);
          
          return {
            ...msg,
            toolStatus: {
              ...msg.toolStatus,
              streamingContent: accumulatedContent,
              isStreaming: !is_complete,
              status: is_complete ? 'success' : 'streaming',
            }
          };
        }
        return msg;
      });
      
      return updatedMessages;
    });
  }, []);

  function handleEvent(event, currentAgentType) {
    if (!event) return;

    if (event.event_type === "status_update") {
      const newStatus = event.content && event.content.status ? event.content.status : "stopped";
      setAgentStatus(newStatus);
      
      // Automatically refresh preview when agent stops
      if (newStatus === "stopped" && toolsPanelRef.current && toolsPanelRef.current.handleRefresh) {
        console.log("Agent stopped - refreshing preview");
        toolsPanelRef.current.handleRefresh();
      }
    } else if (event.event_type === "chat_message") {
      handleTranscriptEvent({
        ...event,
        sender: event.sender || currentAgentType,
      }, currentAgentType);
    } else if (
      (event.event_type === "assistant_delta" || event.event_type === "thinking_delta") &&
      event.sub_agent_info
    ) {
      handleTranscriptEvent(event, currentAgentType);
    } else if (event.event_type === "tool_streaming_update") {
      handleToolStreamingUpdate(event);
    } else if (event.event_type === "system_message") {
      // Handle system messages (e.g., from sandbox creation)
      // Don't add to chat messages - only used for overlay
      // The overlay will pick these up from the WebSocket events directly
      console.log("System message received:", event.content?.text || event.content);
      const message = event.content?.text || event.content;
      setLatestSystemMessage(message);
      
      // Clear the system message after 30 seconds
      setTimeout(() => {
        setLatestSystemMessage(null);
      }, 30000);
    } else if (event.event_type === "log_message") {
      handleLogMessage(event);
    } else if (
      event.event_type === "terminal_command" ||
      event.event_type === "terminal_output"
    ) {
      handleTerminalMessage(event);
    } else if (event.event_type === 'llm_status_update') {
      setShowLLMErrorIndicator(true);
      setLLMErrorIndicatorMessage(event.content.message);
    } else if (event.event_type === 'llm_context_update') {
      // Update context usage when we receive the event
      setContextUsage(event.content);
    } else if (event.event_type === 'memory_suggestions') {
      // Handle memory suggestions from agent
      console.log('[ThreadInterface] Memory suggestions received:', event.content);
      if (event.content && event.content.suggestions && Array.isArray(event.content.suggestions)) {
        console.log('[ThreadInterface] Setting memory suggestions:', event.content.suggestions);
        setMemorySuggestions(event.content.suggestions);
        setDismissedSuggestionIndices(new Set()); // Reset dismissed indices for new batch
      } else {
        console.warn('[ThreadInterface] Invalid memory suggestions format:', event.content);
      }
    }
  }

  const event = useAuthenticatedWebSocket(eventUrl, {
    autoConnect: false,
    onMessage: (data) => handleEvent(data, agentType),
    onOpen: () => {
      setLogs((prev) => [
        ...prev,
        {
          level: "success",
          content: "Event WebSocket connected",
          timestamp: new Date(),
        },
      ]);
    },
    onClose: () => {
      setLogs((prev) => [
        ...prev,
        {
          level: "warning",
          content: "Event WebSocket disconnected",
          timestamp: new Date(),
        },
      ]);
    },
    onError: (error) => {
      setLogs((prev) => [
        ...prev,
        {
          level: "error",
          content: `Event WebSocket error: ${
            error?.message || "Unknown error"
          }`,
          timestamp: new Date(),
        },
      ]);
    },
  });

  useEffect(() => {
    // Manage WebSocket connection based on URL changes and API health
    if (eventUrl && isHealthy) {
      console.log("Connecting WebSocket for URL:", eventUrl);
      event.connect();
      // Clear context usage when switching threads (new WebSocket connection)
      setContextUsage(null);
    } else {
      console.log(
        "Conditions not met for WebSocket connection (URL or Health)"
      );
    }

    // Cleanup function: Disconnect when URL changes or component unmounts
    return () => {
      console.log("Disconnecting WebSocket due to URL change or unmount");
      event.disconnect();
    };
  }, [eventUrl, isHealthy, event.connect, event.disconnect]); // Add connect/disconnect refs to dependencies

  useEffect(() => {
    if (event.messages && Array.isArray(event.messages)) {
      setMessages(event.messages);
    }
  }, [isHealthy]);

  useEffect(() => {
    if (!workspaceIdProp || workspaceFetchedRef.current) {
      return;
    }

    const fetchWorkspaceData = async () => {
      try {
        const workspaceData = await getWorkspaceById(workspaceIdProp);
        if (workspaceData) {
          setWorkspace(workspaceData);
          setProjectPath(workspaceData.directory_path || "Unknown Project Path");
          workspaceFetchedRef.current = true;
          
          console.log(
            "Fetched workspace data:",
            workspaceData
          );

          setLogs((prev) => [
            ...prev,
            {
              level: "info",
              content: `Workspace loaded: ${workspaceIdProp}`,
              timestamp: new Date(),
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching workspace:", error);
        setProjectPath("Error loading path");
        setLogs((prev) => [
          ...prev,
          {
            level: "error",
            content: `Failed to load workspace ${workspaceIdProp}: ${error.message}`,
            timestamp: new Date(),
          },
        ]);
      }
    };

    fetchWorkspaceData();
  }, [workspaceIdProp, getWorkspaceById]);

  // Reset the fetched ref when workspace ID changes
  useEffect(() => {
    workspaceFetchedRef.current = false;
  }, [workspaceIdProp]);

    // Refresh workspace data when sandbox becomes ready (after reset)
  useEffect(() => {
    // Check if sandbox just became ready (after being in loading/creating state)
    if (sandboxStatus.status === 'ready' && workspace) {
      // Only refresh if we haven't already refreshed for this specific sandbox
      const currentSandboxId = sandboxStatus.sandboxId;
      if (currentSandboxId && currentSandboxId !== lastRefreshedSandboxIdRef.current) {
        // Refresh workspace data to get the new sandbox_host
        const refreshWorkspace = async () => {
          try {
            const updatedWorkspaceData = await getWorkspaceById(workspaceIdProp);
            if (updatedWorkspaceData) {
              setWorkspace(updatedWorkspaceData);
              console.log('Refreshed workspace data with new sandbox host:', updatedWorkspaceData.sandbox_host);
              
              // Mark this sandbox as refreshed
              lastRefreshedSandboxIdRef.current = currentSandboxId;
              
              setLogs((prev) => [
                ...prev,
                {
                  level: "info",
                  content: `Sandbox ready - refreshed workspace with new sandbox host`,
                  timestamp: new Date(),
                },
              ]);
            }
          } catch (error) {
            console.error('Failed to refresh workspace data:', error);
            setLogs((prev) => [
              ...prev,
              {
                level: "error",
                content: `Failed to refresh workspace data: ${error.message}`,
                timestamp: new Date(),
              },
            ]);
          }
        };
        
        refreshWorkspace();
      }
    }
  }, [sandboxStatus.status, sandboxStatus.sandboxId, workspaceIdProp, getWorkspaceById, workspace]);

  useEffect(() => {
    if (
      isHealthy &&
      prevHealthyRef.current === false &&
      workspaceIdProp &&
      threadId
    ) {
      if (!event.isConnected) {
        reconnectAttemptRef.current = 0;
        event.connect();

        setLogs((prev) => [
          ...prev,
          {
            level: "info",
            content: "Reconnecting WebSocket after API recovery",
            timestamp: new Date(),
          },
        ]);
      }
    }
    prevHealthyRef.current = isHealthy;
  }, [isHealthy]);

  const handleRemoveFile = useCallback((index) => {
    setUploadedFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const handleFileUpload = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const files = Array.from(e.target.files);
    
    // Filter for supported image types
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const nonImageFiles = files.filter(file => !file.type.startsWith('image/'));

    // Filter out duplicate files (same name and size)
    const newImageFiles = imageFiles.filter(file =>
      !uploadedFiles.some(uploaded =>
        uploaded.name === file.name && uploaded.size === file.size
      )
    );

    const duplicateCount = imageFiles.length - newImageFiles.length;

    if (nonImageFiles.length > 0) {
      setLogs((prev) => [
        ...prev,
        {
          level: "warning",
          content: `${nonImageFiles.length} non-image file(s) were not uploaded. Only image files are currently supported.`,
          timestamp: new Date(),
        },
      ]);

      // Reset file input when non-image files are selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

    if (duplicateCount > 0) {
      setLogs((prev) => [
        ...prev,
        {
          level: "warning",
          content: `${duplicateCount} file(s) already selected and were skipped.`,
          timestamp: new Date(),
        },
      ]);
    }

    if (newImageFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...newImageFiles]);
      
      // Don't create a separate message here - files will be sent with the next message
      setLogs((prev) => [
        ...prev,
        {
          level: "info",
          content: `${imageFiles.length} image file(s) ready to send with next message`,
          timestamp: new Date(),
        },
      ]);
    }
    
    // Reset input value to allow re-selecting the same file or any file after removal
    if (e.target) {
      e.target.value = '';
    }
  };

  const sendMessage = useCallback(
    (message, attachments = []) => {
      console.log("Attempting to send message:", {
        workspaceId: workspaceIdProp,
        isEventConnected: event.isConnected,
        agentType,
        messageLength: message?.length,
        attachmentsCount: attachments?.length,
      });

      setShowLLMErrorIndicator(false);
      setLLMErrorIndicatorMessage('');

      const messageContent =
        typeof message === "string" ? message : String(message);

      const eventMessage = {
        event_type: "chat_message",
        sender: "user",
        content: {
          text: messageContent,
          attachments: attachments, // Include attachments in the content
        },
        timestamp: new Date().toISOString(),
      };

      console.log("Sending message through WebSocket", eventMessage);

      try {
        event.sendMessage(eventMessage);

        setLogs((prev) => [
          ...prev,
          {
            level: "info",
            content: `Sent chat message${attachments.length > 0 ? ` with ${attachments.length} attachment(s)` : ''}`,
            timestamp: new Date(),
          },
        ]);
      } catch (error) {
        console.error("Error sending message through WebSocket:", error);

        setLogs((prev) => [
          ...prev,
          {
            level: "error",
            content: `Error sending message: ${error.message}`,
            timestamp: new Date(),
          },
        ]);
      }
    },
    [workspaceIdProp, event, eventUrl, isHealthy, agentType]
  );

  // Programmatic submit that bypasses input state to avoid stale-closure issues
  const handleProgrammaticSubmit = useCallback(
    async (messageText) => {
      const text = typeof messageText === 'string' ? messageText : '';
      if (!text.trim()) return;

      if (agentStatus === 'generating') return;

      const currentTime = new Date();
      const userMessage = {
        content: text,
        sender: "user",
        timestamp: currentTime,
        lastUpdated: currentTime,
        files: [],
      };

      setMessages((prev) => [...prev, userMessage]);
      sendMessage(text, []);
      setInput("");
    },
    [sendMessage]
  );

  const handleChatSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!input.trim() && uploadedFiles.length === 0) return;

      // Validate file sizes before submission
      const hasOversizedFiles = uploadedFiles.some(file => file.size > MAX_FILE_SIZE);

      if (hasOversizedFiles) {
        setLogs((prev) => [
          ...prev,
          {
            level: "error",
            content: `Cannot send message. Please remove files exceeding ${MAX_FILE_SIZE_IN_MB}MB limit.`,
            timestamp: new Date(),
          },
        ]);
        return;
      }

      const currentTime = new Date();

      // Convert uploaded files to base64
      const attachments = [];
      for (const file of uploadedFiles) {
        if (file.type.startsWith('image/')) {
          try {
            const base64Data = await fileToBase64(file);
            attachments.push({
              type: 'image',
              media_type: file.type,
              data: base64Data,
              filename: file.name
            });
          } catch (error) {
            console.error('Error converting file to base64:', error);
            setLogs((prev) => [
              ...prev,
              {
                level: "error",
                content: `Failed to process image: ${file.name}`,
                timestamp: new Date(),
              },
            ]);
          }
        }
      }

      const userMessage = {
        content: input || `Uploaded ${uploadedFiles.length} image(s)`,
        sender: "user",
        timestamp: currentTime,
        lastUpdated: currentTime,
        files: uploadedFiles,
      };

      setMessages((prev) => [...prev, userMessage]);

      sendMessage(
        input || `Please analyze the attached image${uploadedFiles.length > 1 ? 's' : ''}`, 
        attachments
      );

      setInput("");
      setUploadedFiles([]); // Clear uploaded files after sending
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [input, uploadedFiles, sendMessage]
  );

  const toggleToolExpansion = (messageId) => {
    setExpandedTools((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const handleStopGeneration = useCallback(() => {
    if (event.isConnected && agentStatus === "generating") {
      event.sendMessage({
        event_type: "status_update",
        sender: "user",
        content: {
          action: "interrupt",
          text: "Interrupt requested",
        },
      });

      setLogs((prev) => [
        ...prev,
        {
          level: "info",
          content: "Interrupt request sent to agent",
          timestamp: new Date(),
        },
      ]);
    } else if (
      !event.isConnected &&
      isHealthy &&
      agentStatus === "generating"
    ) {
      event.connect();

      setLogs((prev) => [
        ...prev,
        {
          level: "warning",
          content: "Attempting to reconnect WebSocket to send interrupt",
          timestamp: new Date(),
        },
      ]);

      setTimeout(() => {
        if (event.isConnected && agentStatus === "generating") {
          handleStopGeneration();
        }
      }, 2000);
    }
  }, [agentStatus, event, isHealthy]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();

    const initialMouseX = e.clientX;
    const containerRect = mainContainerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;

    document.body.style.cursor = "col-resize";
    document.body.classList.add("resize-active");

    const handleMouseMoveEvent = (e) => {
      const mouseX = e.clientX - containerRect.left;
      const percentage = Math.min(
        Math.max((mouseX / containerWidth) * 100, 20),
        80
      );
      setSplitWidth(percentage);
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUpEvent = () => {
      document.body.style.cursor = "";
      document.body.classList.remove("resize-active");

      document.removeEventListener("mousemove", handleMouseMoveEvent);
      document.removeEventListener("mouseup", handleMouseUpEvent);
      document.removeEventListener("mouseleave", handleMouseUpEvent);

      setIsDragging(false);
      setCursorPosition(null);
    };

    document.addEventListener("mousemove", handleMouseMoveEvent);
    document.addEventListener("mouseup", handleMouseUpEvent);
    document.addEventListener("mouseleave", handleMouseUpEvent);

    setIsDragging(true);
    setCursorPosition({ x: initialMouseX, y: e.clientY });
  }, []);

  useEffect(() => {
    return () => {
      document.body.classList.remove("resize-active");
    };
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      if (
        chatRef.current.scrollToBottom &&
        chatRef.current.isAutoScrollEnabled !== false
      ) {
        chatRef.current.scrollToBottom();
      } else if (chatRef.current.isAutoScrollEnabled === undefined) {
        const scrollToBottom = () => {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
        };

        scrollToBottom();

        const timeoutId = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (logRef.current && selectedToolTab === "logs") {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }

    if (terminalRef.current && selectedToolTab === "terminal") {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [logs, terminalTabs, activeTerminalTab, selectedToolTab]);

  const resizeStyles = `
    .resize-handle {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 10px;
      cursor: col-resize;
      z-index: 20;
      background: transparent;
      transition: background-color 0.2s;
    }
    
    .resize-handle:hover {
      background-color: rgba(217, 244, 111, 0.3);
    }
    
    .resize-handle.dragging {
      background-color: rgba(217, 244, 111, 0.5);
    }
    
    .resize-line {
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: 2px;
      background-color: rgba(0, 0, 0, 0.1);
      transform: translateX(-50%);
    }

    .dark .resize-line {
      background-color: rgba(255, 255, 255, 0.1);
    }
    
    .resize-handle:hover .resize-line {
      background-color: rgba(0, 0, 0, 0.2);
    }
    
    .dark .resize-handle:hover .resize-line {
      background-color: rgba(255, 255, 255, 0.2);
    }
    
    .resize-handle.dragging .resize-line {
      background-color: rgba(0, 0, 0, 0.3);
    }
    
    .dark .resize-handle.dragging .resize-line {
      background-color: rgba(255, 255, 255, 0.3);
    }
    
    .resize-active {
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }
  `;

  useEffect(() => {
    if (threadId && workspaceIdProp) {
      getThread(workspaceIdProp, threadId, api).then((thread) => {
        if (thread) {
          setTheThread(thread);
          setAgentType(thread.agent_type || "coding_agent");

          getMessages({ getAll: true, includeSubAgentMessages: true }).then((rawMessages) => {
            if (rawMessages) {
              const convertedMessages = convertMessagesToFrontend(rawMessages, thread.agent_type || "coding_agent");
              setMessages(convertedMessages);
              
              if (
                thread.initial_message &&
                thread.auto_submit_first_message &&
                thread.running_jobs.length === 0 &&
                !processedThreadsRef.current.has(threadId)
              ) {
                processedThreadsRef.current.add(threadId);
                
                // Store the message in our ref for later sending when WebSocket connects
                queuedInitialMessageRef.current = {
                  message: thread.initial_message,
                  threadId: threadId
                };
                
                console.log('Initial message queued:', thread.initial_message);
                
                // Add the user message to UI after 500ms delay
                setTimeout(() => {
                  const currentTime = new Date().toISOString();
                  const userMessage = {
                    content: thread.initial_message,
                    sender: "user",
                    timestamp: currentTime,
                    lastUpdated: currentTime,
                  };
                  
                  setMessages(prev => [...prev, userMessage]);
                }, 500);
                
                updateThread(workspaceIdProp, threadId, { 
                  auto_submit_first_message: false
                }, api);
              }
            }
          });

          if (thread.running_jobs.length > 0) {
            console.log("RUNNING JOBS");
            setAgentStatus('generating');
          } else {
            setAgentStatus('stopped');
          }

          console.log(thread);
        }
      });
    }
  }, [threadId, workspaceIdProp]); // Only depend on the actual values that should trigger re-fetch

// Handle WebSocket connection changes and send any queued initial message
useEffect(() => {
  // Check if WebSocket just connected and we have a queued message
  if (event.isConnected && queuedInitialMessageRef.current) {
    console.log('WebSocket connected, sending queued initial message');
    
    // Extract message details
    const { message, threadId: queuedThreadId } = queuedInitialMessageRef.current;
    
    // Only proceed if this is the current thread
    if (queuedThreadId === threadId) {
      // Send the queued message
      sendMessage(message);
      
      // Update thread to prevent re-sending
      updateThread(workspaceIdProp, threadId, { 
        auto_submit_first_message: false 
      }, api);
      
      // Clear the queue after sending
      queuedInitialMessageRef.current = null;
      
      console.log('Queued initial message sent successfully');
    }
  }
}, [event.isConnected, threadId, sendMessage, updateThread]);


  return (
    <div className="flex flex-col h-full">
      <style dangerouslySetInnerHTML={{ __html: styling + resizeStyles }} />

      {/* Main container with fixed height calculation */}
      <div
        className="flex flex-1 overflow-hidden"
        ref={mainContainerRef}
      >
        <ThreadsPanel
          workspaceId={workspaceIdProp}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          data-tour="threads-panel"
          navigate={navigate}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          getAgentTypeLabel={getAgentTypeLabel}
          getAgentTypeIcon={getAgentTypeIcon}
          getAgentTypeColor={getAgentTypeColor}
          threadId={threadId}
        />

        <div className="flex-1 flex flex-col h-full overflow-hidden" data-tour="main-content">
          {/* Mobile Tools Subheader - positioned between ThreadsPanel and right edge */}
          {isMobileView && workspaceIdProp && (
            <MobileToolsSubheaderButtons
              workspace={workspace}
              mobileToolOverlay={mobileToolOverlay}
              handleViewModeClick={(mode) => {
                setSelectedToolTab(mode);
                if (mobileToolOverlay === mode) {
                  setMobileToolOverlay(null);
                } else {
                  setMobileToolOverlay(mode);
                }
              }}
              onOpenProjectSettings={() => {
                openProjectSettings(workspaceIdProp);
              }}
              workspaceDeployedUrl={workspace?.deployed_url}
            />
          )}

          <div className={`flex-1 flex relative overflow-hidden ${isMobileView ? 'p-0' : 'p-6'} min-h-0`} data-tour="chat-view">
              <div
                className={`flex flex-col h-full min-h-0 overflow-hidden relative bg-white dark:bg-neutral-900 ${isMobileView ? 'rounded-none' : 'rounded-2xl'} shadow-sm border border-neutral-200 dark:border-neutral-800`}
                ref={leftPanelRef}
                style={{ width: isMobileView ? '100%' : `${splitWidth}%` }}
                data-tour="chat-area"
              >
                {/* Use MobileToolsSubheader for mobile tool management */}
                {isMobileView && mobileToolOverlay ? (
                  <MobileToolsSubheader
                    sandboxHost={effectiveSandboxHost}
                    onRefresh={() => {
                      if (toolsPanelRef.current && toolsPanelRef.current.handleRefresh) {
                        toolsPanelRef.current.handleRefresh();
                      }
                    }}
                    onOpenInNewWindow={() => {
                      if (effectiveSandboxHost) {
                        window.open(`https://${effectiveSandboxHost}`, '_blank');
                      }
                    }}
                    viewMode={mobileToolOverlay}
                    onViewModeChange={setMobileToolOverlay}
                    workspace={workspace}
                    onOpenProjectSettings={() => {
                      if (workspaceIdProp) {
                        openProjectSettings(workspaceIdProp);
                      }
                    }}
                    mobileToolOverlay={mobileToolOverlay}
                    setMobileToolOverlay={setMobileToolOverlay}
                    workspaceDeployedUrl={workspace?.deployed_url}
                    toolsPanelRef={toolsPanelRef}
                    logs={logs}
                    terminalTabs={terminalTabs}
                    activeTerminalTab={activeTerminalTab}
                    setActiveTerminalTab={setActiveTerminalTab}
                    setTerminalTabs={setTerminalTabs}
                    agentStatus={agentStatus}
                    logRef={logRef}
                    terminalRef={terminalRef}
                    messages={messages}
                    setInput={setInput}
                    handleProgrammaticSubmit={handleProgrammaticSubmit}
                  />
                ) : (
                <ChatArea
                  messages={messages}
                  input={input}
                  setInput={setInput}
                  handleChatSubmit={handleChatSubmit}
                  fileInputRef={fileInputRef}
                  handleFileUpload={handleFileUpload}
                  removeFile={handleRemoveFile}
                  chatRef={chatRef}
                  agentStatus={agentStatus}
                  isEventConnected={event.isConnected}
                  toggleToolExpansion={toggleToolExpansion}
                  expandedTools={expandedTools}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  loading={messagesLoading}
                  error={messagesError}
                  handleStopGeneration={handleStopGeneration}
                  latestSystemMessage={latestSystemMessage}
                  showLLMErrorIndicator={showLLMErrorIndicator}
                  LLMErrorIndicatorMessage={LLMErrorIndicatorMessage}
                  contextUsage={contextUsage}
                  createThread={createThread}
                  navigate={navigate}
                  workspaceId={workspaceIdProp}
                  currentAgentType={getAgentTypeLabel(agentType)}
                  isSandboxReady={isSandboxReady}
                  mobileToolOverlay={mobileToolOverlay}
                  setMobileToolOverlay={setMobileToolOverlay}
                  readOnly={isReadOnly}
                />
                )}
                
                {!isMobileView && (
                <div
                  className={`resize-handle ${isDragging ? "dragging" : ""}`}
                  ref={resizerRef}
                  onMouseDown={handleMouseDown}
                  style={{
                    right: -5,
                  }}
                >
                  <div className="resize-line"></div>
                  {isDragging && cursorPosition && (
                    <div
                      className="absolute w-px h-full bg-brand-primary"
                      style={{
                        left: "50%",
                      }}
                    />
                  )}
                </div>
                )}
              </div>

              {!isMobileView && (
              <div className="ml-3 bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-800 min-w-0" style={{ width: `${100 - splitWidth}%` }} data-tour="tools-panel">
                <ToolsPanel
                  ref={toolsPanelRef}
                  selectedToolTab={selectedToolTab}
                  setSelectedToolTab={setSelectedToolTab}
                  logs={logs}
                  terminalTabs={terminalTabs}
                  activeTerminalTab={activeTerminalTab}
                  setActiveTerminalTab={setActiveTerminalTab}
                  setTerminalTabs={setTerminalTabs}
                  agentStatus={agentStatus}
                  logRef={logRef}
                  terminalRef={terminalRef}
                  isEventConnected={event.isConnected}
                  splitWidth={100}
                  messages={messages}
                sandboxHost={effectiveSandboxHost}
                sandboxStatus={sandboxStatus}
                  workspaceId={workspaceIdProp}
                  workspace={workspace}
                  setInput={setInput}
                  handleProgrammaticSubmit={handleProgrammaticSubmit}
                />
              </div>
              )}
          </div>
        </div>
      </div>
      
      {/* Help System */}
      <HelpSystem pageName="threadInterface" />

      {/* Memory Suggestion Popup */}
      {memorySuggestions.length > 0 && (
        <MemorySuggestionPopup
          suggestions={memorySuggestions}
          workspaceId={workspaceIdProp}
          api={api}
          onDismiss={(index) => {
            setDismissedSuggestionIndices((prev) => new Set(prev).add(index));
            // Clear all if all dismissed
            if (dismissedSuggestionIndices.size + 1 === memorySuggestions.length) {
              setMemorySuggestions([]);
            }
          }}
          onDismissAll={() => {
            setMemorySuggestions([]);
            setDismissedSuggestionIndices(new Set());
          }}
          onSave={(index) => {
            setDismissedSuggestionIndices((prev) => new Set(prev).add(index));
            // Clear all if all saved
            if (dismissedSuggestionIndices.size + 1 === memorySuggestions.length) {
              setMemorySuggestions([]);
            }
          }}
        />
      )}
    </div>
  );
};

ThreadInterface.propTypes = {
  workspaceId: PropTypes.string.isRequired,
  threadId: PropTypes.string,
};

export default ThreadInterface;
